import { useQuery } from '@tanstack/react-query';
import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '~/components/Spinner';
import { cn } from '~/lib/cn';

import {
  getStreamsByVintageSeries,
  getStreamsByVintageTotals,
} from '../../../api/streams-by-vintage';
import { retryOn504Options } from '../../../constants/api';
import { useAmplifyUser } from '../../../context/useAmplifyUser';
import { NoAccess } from '../../LabelPerf/components';
import type { SeriesConfig } from '../Components/MultiLineChart';
import { MultiLineChart } from '../Components/MultiLineChart';
import { SummaryTable } from '../Components/SummaryTable';
import type { VintageTableRow } from './types';
import { VINTAGE_LINE_COLORS } from './types';

// Line chart series configuration builder (only 3 series shown in chart, not unreconciled)
const getChartSeries = (t: TFunction): SeriesConfig[] => [
  {
    key: 'newRelease',
    color: VINTAGE_LINE_COLORS.newRelease,
    label: t('reports.vintages.newRelease'),
  },
  {
    key: 'shallow',
    color: VINTAGE_LINE_COLORS.shallow,
    label: t('reports.vintages.shallowCatalog'),
  },
  {
    key: 'deepCatalog',
    color: VINTAGE_LINE_COLORS.deepCatalog,
    label: t('reports.vintages.deepCatalog'),
  },
];

// Helper functions - format number with appropriate suffix (values are already in billions)
const formatNumber = (value: number): string => {
  const absValue = Math.abs(value);

  // Values are in billions, so 1000 = 1T
  if (absValue >= 1000) {
    return (value / 1000).toFixed(1) + 'T';
  }

  if (absValue >= 1) {
    return value.toFixed(1) + 'B';
  }

  // Less than 1B = show in millions
  if (absValue >= 0.001) {
    return (value * 1000).toFixed(1) + 'M';
  }

  // Less than 1M = show in thousands
  if (absValue >= 0.000001) {
    return (value * 1000000).toFixed(1) + 'K';
  }

  return value.toFixed(1);
};

const formatChange = (value: number): string => {
  return value.toFixed(1) + '%';
};

const formatShareDiff = (value: number): string => {
  const sign = value >= 0 ? '' : '';

  return sign + value.toFixed(1) + '%';
};

// Table row type with isTotal flag
interface TableRowWithTotal extends VintageTableRow {
  isTotal?: boolean;
}

// Column cell render functions
const renderVintageCell = (info: CellContext<TableRowWithTotal, unknown>) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {info.row.original.vintage}
    </span>
  );
};

const renderThisWeekCell = (info: CellContext<TableRowWithTotal, unknown>) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {formatNumber(info.row.original.thisWeek)}
    </span>
  );
};

const renderFytdCell = (info: CellContext<TableRowWithTotal, unknown>) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {formatNumber(info.row.original.fytd)}
    </span>
  );
};

const renderChangeCell = (info: CellContext<TableRowWithTotal, unknown>) => {
  const value = info.row.original.fytdChange;
  const isTotal = info.row.original.isTotal;

  return (
    <span
      className={cn(
        value >= 0 ? 'text-success' : 'text-error-default',
        isTotal && 'font-bold',
      )}
    >
      {formatChange(value)}
    </span>
  );
};

const renderFytdPercentCell = (
  info: CellContext<TableRowWithTotal, unknown>,
) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {info.row.original.fytdPercent}%
    </span>
  );
};

const renderFytdPrevPercentCell = (
  info: CellContext<TableRowWithTotal, unknown>,
) => {
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn('text-default', isTotal && 'font-bold')}>
      {info.row.original.fytdPrevPercent}%
    </span>
  );
};

const getShareDiffColor = (value: number): string => {
  if (value > 0) {
    return 'text-success';
  }

  if (value < 0) {
    return 'text-error-default';
  }

  return 'text-default';
};

const renderShareDiffCell = (info: CellContext<TableRowWithTotal, unknown>) => {
  const value = info.row.original.shareDiff;
  const isTotal = info.row.original.isTotal;

  return (
    <span className={cn(getShareDiffColor(value), isTotal && 'font-bold')}>
      {formatShareDiff(value)}
    </span>
  );
};

export function GlobalStreamsByVintage() {
  const { t } = useTranslation('label-perf');
  const { user, isLoading: isUserLoading } = useAmplifyUser();

  // Check if user has admin or supervisor role
  const hasPermission = user?.role === 'admin' || user?.role === 'supervisor';

  // Memoize chart series with translations
  const chartSeries = useMemo(() => getChartSeries(t), [t]);

  // Fetch streams by vintage series data (for line chart)
  const {
    data: streamsSeriesData,
    isLoading: isStreamsSeriesLoading,
    isError: isStreamsSeriesError,
  } = useQuery({
    queryKey: ['streams-by-vintage-series'],
    queryFn: getStreamsByVintageSeries,
    ...retryOn504Options,
  });

  // Fetch streams by vintage totals data (for table)
  const {
    data: tableTotalsData,
    isLoading: isTableTotalsLoading,
    isError: isTableTotalsError,
  } = useQuery({
    queryKey: ['streams-by-vintage-totals'],
    queryFn: getStreamsByVintageTotals,
    ...retryOn504Options,
  });

  // Process streams series chart data with dynamic Y-axis
  const { streamsChartData, streamsYMax, streamsYTicks } = useMemo(() => {
    if (!streamsSeriesData || streamsSeriesData.length === 0) {
      return {
        streamsChartData: [],
        streamsYMax: 8,
        streamsYTicks: [0, 2, 4, 6, 8],
      };
    }

    // Find max value for Y-axis
    let maxVal = 0;

    streamsSeriesData.forEach((point) => {
      const values = [point.newRelease, point.shallow, point.deepCatalog];

      values.forEach((v) => {
        if (v > maxVal) {
          maxVal = v;
        }
      });
    });

    // Round up to nice number
    const roundedMax = Math.ceil(maxVal / 2) * 2;
    const computedMaxY = Math.max(roundedMax, 8);
    const step = computedMaxY / 4;
    const ticks = Array.from({ length: 5 }, (_, i) => i * step);

    return {
      streamsChartData: streamsSeriesData,
      streamsYMax: computedMaxY,
      streamsYTicks: ticks,
    };
  }, [streamsSeriesData]);

  // Table columns
  const columns = useMemo<ColumnDef<TableRowWithTotal>[]>(
    () => [
      {
        accessorKey: 'vintage',
        header: t('reports.tables.columns.vintage'),
        cell: renderVintageCell,
        size: 180,
      },
      {
        accessorKey: 'thisWeek',
        header: t('reports.tables.columns.thisWeek'),
        cell: renderThisWeekCell,
        size: 100,
      },
      {
        accessorKey: 'fytd',
        header: t('reports.tables.columns.fytd'),
        cell: renderFytdCell,
        size: 100,
      },
      {
        accessorKey: 'fytdChange',
        header: t('reports.tables.columns.fytdChange'),
        cell: renderChangeCell,
        size: 100,
      },
      {
        accessorKey: 'fytdPercent',
        header: t('reports.tables.columns.fytdPercent'),
        cell: renderFytdPercentCell,
        size: 100,
      },
      {
        accessorKey: 'fytdPrevPercent',
        header: t('reports.tables.columns.fytdPrevPercent'),
        cell: renderFytdPrevPercentCell,
        size: 100,
      },
      {
        accessorKey: 'shareDiff',
        header: t('reports.tables.columns.shareDiff'),
        cell: renderShareDiffCell,
        size: 100,
      },
    ],
    [t],
  );

  // Process table data from API
  const tableData = useMemo<TableRowWithTotal[]>(() => {
    if (!tableTotalsData || tableTotalsData.length === 0) {
      return [];
    }

    // Map API data to table row format and sort by order index
    const rows: TableRowWithTotal[] = tableTotalsData
      .sort((a, b) => a.orderIndex - b.orderIndex)
      .map((item) => ({
        vintage: item.vintage,
        thisWeek: item.thisWeek / 1_000_000_000, // Convert to billions
        fytd: item.fytd / 1_000_000_000, // Convert to billions
        fytdChange: item.fytdChange,
        fytdPercent: item.fytdPercent,
        fytdPrevPercent: item.fytdPrevPercent,
        shareDiff: item.shareDiff,
        isTotal: false,
      }));

    // Calculate totals row
    const totalThisWeek = tableTotalsData.reduce(
      (sum, item) => sum + item.thisWeek,
      0,
    );
    const totalFytd = tableTotalsData.reduce((sum, item) => sum + item.fytd, 0);
    const totalFytdPrev = tableTotalsData.reduce(
      (sum, item) => sum + item.fytdPrev,
      0,
    );
    const totalFytdChange =
      totalFytdPrev > 0
        ? ((totalFytd - totalFytdPrev) / totalFytdPrev) * 100
        : 0;

    rows.push({
      vintage: t('common.total'),
      thisWeek: totalThisWeek / 1_000_000_000,
      fytd: totalFytd / 1_000_000_000,
      fytdChange: Number(totalFytdChange.toFixed(1)),
      fytdPercent: 100,
      fytdPrevPercent: 100,
      shareDiff: 0,
      isTotal: true,
    });

    return rows;
  }, [tableTotalsData, t]);

  const isLoading =
    isUserLoading || isStreamsSeriesLoading || isTableTotalsLoading;
  const isError = isStreamsSeriesError || isTableTotalsError;

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <Spinner size="md" />
      </div>
    );
  }

  // Permission check - only admin and supervisor roles can access
  if (!hasPermission) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient bg-[length:100%] bg-fixed bg-no-repeat bg-level-01">
        <NoAccess />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient bg-[length:100%] bg-fixed bg-no-repeat bg-level-01">
        <div className="text-center">
          <h2 className="mb-sm text-headline-sm text-default">
            {t('error.failedToLoadData', 'Failed to load data')}
          </h2>
          <p className="text-subtle">
            {t(
              'error.refreshOrContactSupport',
              'Please try refreshing the page or contact support if the issue persists.',
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen min-w-[800px] grow overflow-auto overscroll-contain bg-gradient bg-[length:100%] bg-fixed bg-no-repeat bg-level-01">
      <div className="p-md sm:p-lg md:p-xl">
        {/* Header */}
        <h1 className="mb-2xl text-headline-sm font-bold text-default">
          {t('reports.pages.globalStreamsByVintage')}
        </h1>

        {/* Line Chart Section - Streams by Vintage */}
        <div className="mb-2xl">
          <MultiLineChart
            data={streamsChartData}
            series={chartSeries}
            yAxisLabel={t('reports.charts.streams')}
            height={380}
            yTickValues={streamsYTicks}
            yMax={streamsYMax}
          />
        </div>

        {/* Table Section - Streams by Vintage Totals */}
        <SummaryTable
          data={tableData}
          columns={columns}
          ariaLabel={t('reports.tables.ariaLabels.globalStreamsByVintage')}
          maxWidth="850px"
        />
      </div>
    </div>
  );
}

GlobalStreamsByVintage.displayName = 'GlobalStreamsByVintage';
